// src/store/slices/customTicksSlice.ts
/**
 * Custom Ticks state management for T-10
 * 
 * Manages user-inserted custom ticks like midpoints.
 * These ticks are treated as major ticks and get labels.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomTick, CustomTicksState } from '../../types/redux';

const initialState: CustomTicksState = {
  ticks: [],
  nextId: 1,
};

export const customTicksSlice = createSlice({
  name: 'customTicks',
  initialState,
  reducers: {
    /**
     * Add a new custom tick (e.g., midpoint)
     */
    addCustomTick: (state, action: PayloadAction<{
      value: number;
      type?: 'midpoint' | 'custom';
      label?: string;
      equivalentDenominator?: number;
    }>) => {
      const { value, type = 'custom', label, equivalentDenominator } = action.payload;
      
      // Check if a tick already exists very close to this value
      const existingTick = state.ticks.find(tick => 
        Math.abs(tick.value - value) < 1e-10 &&
        (tick.equivalentDenominator ?? null) === (equivalentDenominator ?? null)
      );
      
      if (!existingTick) {
        state.ticks.push({
          id: `custom-${state.nextId}`,
          value,
          label,
          type,
          createdAt: Date.now(),
          equivalentDenominator: equivalentDenominator ?? undefined,
        });
        state.nextId += 1;
      }
    },

    /**
     * Remove a custom tick by ID
     */
    removeCustomTick: (state, action: PayloadAction<string>) => {
      state.ticks = state.ticks.filter(tick => tick.id !== action.payload);
    },

    /**
     * Remove a custom tick by value (within tolerance)
     */
    removeCustomTickByValue: (state, action: PayloadAction<{ value: number; equivalentDenominator?: number } | number>) => {
      const payload = action.payload;
      const value = typeof payload === 'number' ? payload : payload.value;
      const equivalentDenominator = typeof payload === 'number' ? undefined : payload.equivalentDenominator;
      state.ticks = state.ticks.filter(tick => 
        Math.abs(tick.value - value) >= 1e-10 ||
        (tick.equivalentDenominator ?? null) !== (equivalentDenominator ?? null)
      );
    },

    /**
     * Update custom tick label
     */
    updateCustomTickLabel: (state, action: PayloadAction<{
      id: string;
      label: string;
    }>) => {
      const { id, label } = action.payload;
      const tick = state.ticks.find(t => t.id === id);
      if (tick) {
        tick.label = label;
      }
    },

    /**
     * Clear all custom ticks
     */
    clearCustomTicks: (state) => {
      state.ticks = [];
    },

    /**
     * Clear only midpoint ticks (keep other custom ticks)
     */
    clearMidpointTicks: (state) => {
      state.ticks = state.ticks.filter(tick => tick.type !== 'midpoint');
    },

    /**
     * Add midpoint between two values
     */
    addMidpoint: (state, action: PayloadAction<{
      startValue: number;
      endValue: number;
      equivalentDenominator?: number;
    }>) => {
      const { startValue, endValue, equivalentDenominator } = action.payload;
      // FIX: Round midpoint calculation to avoid floating point precision errors
      const rawMidpoint = (startValue + endValue) / 2;
      const midpointValue = Math.round(rawMidpoint * 1e10) / 1e10; // Round to 10 decimal places
      
      // Check if midpoint already exists
      const existingMidpoint = state.ticks.find(tick => 
        Math.abs(tick.value - midpointValue) < 1e-10 &&
        (tick.equivalentDenominator ?? null) === (equivalentDenominator ?? null)
      );
      
      if (!existingMidpoint) {
        state.ticks.push({
          id: `midpoint-${state.nextId}`,
          value: midpointValue,
          type: 'midpoint',
          createdAt: Date.now(),
          equivalentDenominator: equivalentDenominator ?? undefined,
        });
        state.nextId += 1;
      }
    },

    /**
     * Load custom ticks state from shared scene
     */
    loadCustomTicksState: (state, action: PayloadAction<CustomTicksState>) => {
      state.ticks = action.payload.ticks || [];
      state.nextId = action.payload.nextId || 1;
    },
  },
});

export const {
  addCustomTick,
  removeCustomTick,
  removeCustomTickByValue,
  updateCustomTickLabel,
  clearCustomTicks,
  clearMidpointTicks,
  addMidpoint,
  loadCustomTicksState,
} = customTicksSlice.actions;

export default customTicksSlice.reducer;

// Selectors
export const selectCustomTicksInDomain = (
  customTicks: CustomTicksState,
  domain: { start: number; end: number },
  equivalentDenominator?: number
) => {
  return customTicks.ticks.filter(
    (tick) =>
      tick.value >= domain.start &&
      tick.value <= domain.end &&
      (equivalentDenominator === undefined ||
        (tick.equivalentDenominator ?? null) === (equivalentDenominator ?? null))
  );
};

export const selectMidpointTicks = (customTicks: CustomTicksState) => {
  return customTicks.ticks.filter(tick => tick.type === 'midpoint');
};

export const selectCustomTicksSorted = (customTicks: CustomTicksState) => {
  return [...customTicks.ticks].sort((a, b) => a.value - b.value);
};